import type { Message as UIMessage } from '@ai-sdk/react';

type ToolInvocationState = 'partial-call' | 'call' | 'result';
interface ToolInvocation {
  state: ToolInvocationState;
  toolCallId: string;
  toolName: string;
  args: unknown;
  result?: unknown;
}
import { cn } from '@/lib/utils';
import { User, Bot, Search, FileText } from 'lucide-react';
import type {
  AssistantProposedAction,
  AssistantClientSelection,
  AssistantClientSelectionCandidate,
  AssistantCaseSelection,
  AssistantCaseSelectionCandidate,
  AssistantTransactionSelection,
  AssistantDocumentTemplateSelection,
  AssistantDocumentTemplateCandidate,
} from '@/lib/api-client';
import { ActionPreviewCard } from './action-preview-card';
import { ClientSelectionCard } from './client-selection-card';
import { CaseSelectionCard } from './case-selection-card';
import { TransactionSelectionCard } from './transaction-selection-card';
import { DocumentTemplateSelectionCard } from './document-template-selection-card';

/**
 * Type guard: um tool result e uma acao proposta (create_client) que precisa de
 * confirmacao humana. A tool de escrita devolve este shape; o front renderiza
 * um card de confirmacao em vez de um resumo de leitura.
 */
function isProposedAction(result: unknown): result is AssistantProposedAction {
  if (!result || typeof result !== 'object') return false;
  const r = result as Record<string, unknown>;
  return (
    r.type === 'proposed_action' &&
    typeof r.proposalId === 'string' &&
    typeof r.preview === 'object' &&
    r.preview !== null
  );
}

/**
 * Type guard: o resultado de select_client quando ha mais de um cliente com
 * nome ambiguo (envelope client_selection). Com 1 resultado a tool devolve o
 * cliente direto (sem este envelope) — o modelo usa sem exigir escolha.
 */
function isClientSelection(result: unknown): result is AssistantClientSelection {
  if (!result || typeof result !== 'object') return false;
  const r = result as Record<string, unknown>;
  return r.type === 'client_selection' && Array.isArray(r.candidates);
}

/**
 * Type guard: o resultado de select_case quando o cliente tem mais de um
 * processo (envelope case_selection). Com 1 resultado a tool devolve o
 * processo direto (sem este envelope) — o modelo usa sem exigir escolha.
 */
function isCaseSelection(result: unknown): result is AssistantCaseSelection {
  if (!result || typeof result !== 'object') return false;
  const r = result as Record<string, unknown>;
  return r.type === 'case_selection' && Array.isArray(r.candidates);
}

/**
 * Type guard: o resultado de select_transaction (card de selecao de
 * lancamento para EXCLUSAO). Ao contrario de client_selection, este envelope
 * aparece SEMPRE que ha ao menos 1 candidato (acao destrutiva — o usuario
 * sempre confirma clicando, mesmo com 1 unico resultado).
 */
function isTransactionSelection(result: unknown): result is AssistantTransactionSelection {
  if (!result || typeof result !== 'object') return false;
  const r = result as Record<string, unknown>;
  return r.type === 'transaction_selection' && Array.isArray(r.candidates);
}

/**
 * Type guard: o resultado de select_document_template quando ha mais de um
 * modelo compativel. Com um unico modelo a tool devolve o modelo direto (sem
 * este envelope), igual ao select_client.
 */
function isDocumentTemplateSelection(
  result: unknown,
): result is AssistantDocumentTemplateSelection {
  if (!result || typeof result !== 'object') return false;
  const r = result as Record<string, unknown>;
  return r.type === 'document_template_selection' && Array.isArray(r.candidates);
}

interface AssistantMessageProps {
  message: UIMessage;
  /**
   * Chamado quando o usuario clica num candidato do card de desambiguacao de
   * cliente. O caller (AssistantWidget) reinjeta a escolha no chat via o
   * mesmo fluxo de envio de mensagem (append do useChat).
   */
  onSelectClient?: (candidate: AssistantClientSelectionCandidate) => void;
  /**
   * Idem para o card de escolha de PROCESSO filtrado por cliente
   * (select_case): a escolha volta ao chat para o modelo continuar o fluxo
   * (ex: inspecionar variaveis do documento com o caseId resolvido).
   */
  onSelectCase?: (candidate: AssistantCaseSelectionCandidate) => void;
  /**
   * Idem para o card de escolha de MODELO de documento: a escolha volta ao
   * chat para o modelo seguir o fluxo (inspecionar variaveis -> perguntar o
   * que falta -> propor a geracao).
   */
  onSelectTemplate?: (candidate: AssistantDocumentTemplateCandidate) => void;
}

/** Renderiza um tool call de forma legivel. */
function ToolCallBadge({ toolName, args }: { toolName: string; args: unknown }) {
  const labels: Record<string, string> = {
    search_clients: 'Buscando clientes',
    get_client: 'Consultando cliente',
    select_client: 'Verificando qual cliente',
    search_cases: 'Buscando processos',
    get_case: 'Consultando processo',
    select_case: 'Verificando qual processo',
    create_client: 'Preparando cadastro de cliente',
    create_financial_entry: 'Preparando lançamento financeiro',
    create_financial_category: 'Preparando categoria financeira',
    select_transaction: 'Buscando lançamento para excluir',
    select_document_template: 'Procurando o modelo de documento',
    inspect_template_variables: 'Conferindo o que o modelo precisa',
    generate_document: 'Preparando o documento',
    list_template_variables: 'Consultando as variáveis disponíveis',
    create_document_template: 'Redigindo o modelo',
    count_cases_by_action: 'Contando processos por tipo de ação',
    financial_summary: 'Consultando o financeiro do mês',
    financial_totals: 'Calculando totais financeiros',
    list_transactions: 'Listando lançamentos financeiros',
    list_financial_categories: 'Consultando categorias financeiras',
    list_tasks: 'Buscando tarefas',
    list_deadlines: 'Verificando prazos',
    get_agenda: 'Consultando a agenda',
    list_intimations: 'Buscando intimações',
    lookup_case_datajud: 'Consultando o DataJud',
    list_action_types: 'Consultando tipos de ação',
    create_case: 'Preparando cadastro de processo',
    list_team_members: 'Consultando membros do escritório',
    create_task: 'Preparando tarefa',
  };

  const argStr = (() => {
    if (!args || typeof args !== 'object') return '';
    const a = args as Record<string, unknown>;
    if (a.query) return String(a.query);
    if (a.clientId) return String(a.clientId).slice(0, 8) + '...';
    if (a.caseId) return String(a.caseId).slice(0, 8) + '...';
    if (a.numeroCnj) return String(a.numeroCnj);
    return '';
  })();

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--accent)] border border-[color-mix(in_oklch,var(--accent)_25%,transparent)]">
      <Search className="h-3 w-3 shrink-0" />
      {labels[toolName] ?? toolName}
      {argStr ? `: ${argStr}` : ''}
    </span>
  );
}

/** Resumo enxuto de um tool result. */
function ToolResultSummary({ toolName, result }: { toolName: string; result: unknown }) {
  if (!result || typeof result !== 'object') return null;
  const r = result as Record<string, unknown>;

  if (toolName === 'search_clients' || toolName === 'search_cases') {
    const items = Array.isArray(r.items) ? r.items : [];
    const total = typeof r.total === 'number' ? r.total : items.length;
    const label = toolName === 'search_clients' ? 'cliente(s)' : 'processo(s)';
    if (total === 0) {
      return (
        <span className="text-xs text-[var(--fg-muted)] italic">
          Nenhum {label === 'cliente(s)' ? 'cliente' : 'processo'} encontrado.
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[var(--fg-muted)]">
        <FileText className="h-3 w-3 shrink-0" />
        {total} {label} encontrado{total !== 1 ? 's' : ''}.
      </span>
    );
  }

  if (toolName === 'get_client') {
    const name = typeof r.name === 'string' ? r.name : null;
    if (name) {
      return (
        <span className="text-xs text-[var(--fg-muted)]">
          Cliente: {name}
        </span>
      );
    }
  }

  if (toolName === 'select_client') {
    // client_selection (>1 candidato) ja renderiza o card proprio no lugar
    // deste resumo — aqui so cobrimos os outros dois desfechos da tool.
    if (typeof r.error === 'string') {
      return <span className="text-xs text-[var(--fg-muted)] italic">{r.error}</span>;
    }
    const client = r.client as Record<string, unknown> | undefined;
    const name = typeof client?.name === 'string' ? client.name : null;
    if (name) {
      return (
        <span className="text-xs text-[var(--fg-muted)]">
          Cliente identificado: {name}
        </span>
      );
    }
  }

  if (toolName === 'select_transaction') {
    // transaction_selection (>=1 candidato) ja renderiza o card proprio no
    // lugar deste resumo — aqui so cobrimos o desfecho de erro (0 candidatos).
    if (typeof r.error === 'string') {
      return <span className="text-xs text-[var(--fg-muted)] italic">{r.error}</span>;
    }
  }

  if (toolName === 'select_document_template') {
    // document_template_selection (>1 candidato) ja renderiza o card proprio;
    // aqui cobrimos os outros dois desfechos: erro e modelo unico.
    if (typeof r.error === 'string') {
      return <span className="text-xs text-[var(--fg-muted)] italic">{r.error}</span>;
    }
    const template = r.template as Record<string, unknown> | undefined;
    const name = typeof template?.name === 'string' ? template.name : null;
    if (name) {
      return (
        <span className="text-xs text-[var(--fg-muted)]">Modelo: {name}</span>
      );
    }
  }

  if (toolName === 'inspect_template_variables') {
    const total = typeof r.totalVariables === 'number' ? r.totalVariables : null;
    const missing = Array.isArray(r.missing) ? r.missing.length : null;
    if (total !== null && missing !== null) {
      return (
        <span className="text-xs text-[var(--fg-muted)]">
          {total} variável(is) no modelo
          {missing > 0 ? `, ${missing} a preencher` : ', nada faltando'}.
        </span>
      );
    }
  }

  if (toolName === 'select_case') {
    // case_selection (>1 candidato) ja renderiza o card proprio no lugar
    // deste resumo — aqui so cobrimos os outros dois desfechos da tool.
    if (typeof r.error === 'string') {
      return <span className="text-xs text-[var(--fg-muted)] italic">{r.error}</span>;
    }
    const kase = r.case as Record<string, unknown> | undefined;
    const numeroCnj = typeof kase?.numeroCnj === 'string' ? kase.numeroCnj : null;
    if (numeroCnj) {
      return (
        <span className="text-xs text-[var(--fg-muted)]">
          Processo identificado: {numeroCnj}
        </span>
      );
    }
  }

  if (toolName === 'get_case') {
    const cnj = typeof r.numeroCnj === 'string' ? r.numeroCnj : null;
    const status = typeof r.status === 'string' ? r.status : null;
    if (cnj) {
      return (
        <span className="text-xs text-[var(--fg-muted)]">
          Processo: {cnj}{status ? ` (${status})` : ''}
        </span>
      );
    }
  }

  if (toolName === 'lookup_case_datajud') {
    if (r.found === false) {
      return (
        <span className="text-xs text-[var(--fg-muted)] italic">
          Processo não encontrado no DataJud.
        </span>
      );
    }
    const tribunal = typeof r.tribunalAbbreviation === 'string' ? r.tribunalAbbreviation : null;
    if (r.found === true) {
      return (
        <span className="text-xs text-[var(--fg-muted)]">
          Processo localizado{tribunal ? ` (${tribunal})` : ''} no DataJud.
        </span>
      );
    }
  }

  if (toolName === 'list_action_types') {
    const items = Array.isArray(r.items) ? r.items : [];
    return (
      <span className="text-xs text-[var(--fg-muted)]">
        {items.length} tipo{items.length !== 1 ? 's' : ''} de ação encontrado{items.length !== 1 ? 's' : ''}.
      </span>
    );
  }

  if (toolName === 'list_team_members') {
    const items = Array.isArray(r.items) ? r.items : [];
    return (
      <span className="text-xs text-[var(--fg-muted)]">
        {items.length} membro{items.length !== 1 ? 's' : ''} do escritório.
      </span>
    );
  }

  return null;
}

export function AssistantMessage({
  message,
  onSelectClient,
  onSelectCase,
  onSelectTemplate,
}: AssistantMessageProps) {
  const isUser = message.role === 'user';
  const toolInvocations = message.toolInvocations;

  return (
    <div
      className={cn(
        'flex gap-2.5',
        isUser ? 'flex-row-reverse' : 'flex-row',
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs',
          isUser
            ? 'bg-[var(--accent)] text-[var(--accent-fg)]'
            : 'bg-[color-mix(in_oklch,var(--fg)_10%,transparent)] text-[var(--fg-muted)]',
        )}
      >
        {isUser ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
      </div>

      {/* Conteudo */}
      <div
        className={cn(
          'flex max-w-[80%] flex-col gap-1.5',
          isUser ? 'items-end' : 'items-start',
        )}
      >
        {/* Miniaturas de imagens anexadas (rodada 2 "anexos e voz") — so a
            mensagem do usuario carrega experimental_attachments; mensagens do
            assistente nunca tem. */}
        {isUser && message.experimental_attachments && message.experimental_attachments.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1.5">
            {message.experimental_attachments
              .filter((att) => att.contentType?.startsWith('image/'))
              .map((att, i) => (
                <img
                  key={`${message.id}-att-${i}`}
                  src={att.url}
                  alt={att.name ?? 'Imagem anexada'}
                  className="h-16 w-16 rounded-lg border border-[var(--border)] object-cover"
                />
              ))}
          </div>
        )}

        {/* Tool calls */}
        {toolInvocations && toolInvocations.length > 0 && (
          <div className="flex flex-col gap-1">
            {toolInvocations.map((inv: ToolInvocation) => {
              // Acao de escrita proposta: renderiza o card de confirmacao
              // (gate humano), nao um resumo de leitura. O card discrimina o
              // kind pelo campo `action`.
              if (inv.state === 'result' && isProposedAction(inv.result)) {
                return (
                  <div key={inv.toolCallId} className="w-full max-w-[280px]">
                    <ActionPreviewCard action={inv.result} />
                  </div>
                );
              }
              // select_client com mais de um candidato: renderiza o card de
              // desambiguacao clicavel em vez do resumo padrao de leitura.
              if (inv.state === 'result' && isClientSelection(inv.result)) {
                return (
                  <div key={inv.toolCallId} className="w-full max-w-[280px]">
                    <ClientSelectionCard
                      selection={inv.result}
                      onSelect={(candidate) => onSelectClient?.(candidate)}
                    />
                  </div>
                );
              }
              // select_case com mais de um processo do cliente: card de
              // escolha clicavel. Como no client_selection, o clique
              // reinjeta a escolha no chat — o modelo precisa do caseId
              // para continuar o fluxo (ex: gerar documento).
              if (inv.state === 'result' && isCaseSelection(inv.result)) {
                return (
                  <div key={inv.toolCallId} className="w-full max-w-[280px]">
                    <CaseSelectionCard
                      selection={inv.result}
                      onSelect={(candidate) => onSelectCase?.(candidate)}
                    />
                  </div>
                );
              }
              // select_transaction (candidatos para EXCLUSAO): renderiza o
              // card de selecao clicavel. Diferente de client_selection, o
              // clique aqui NAO reinjeta nada no chat — o card chama o
              // endpoint de proposta direto e evolui pro card de confirmacao
              // destrutivo sozinho (ver transaction-selection-card.tsx).
              if (inv.state === 'result' && isTransactionSelection(inv.result)) {
                return (
                  <div key={inv.toolCallId} className="w-full max-w-[280px]">
                    <TransactionSelectionCard selection={inv.result} />
                  </div>
                );
              }
              // select_document_template com mais de um modelo: card de
              // escolha clicavel. Como no client_selection, o clique reinjeta
              // a escolha no chat — o modelo precisa dela para continuar.
              if (inv.state === 'result' && isDocumentTemplateSelection(inv.result)) {
                return (
                  <div key={inv.toolCallId} className="w-full max-w-[280px]">
                    <DocumentTemplateSelectionCard
                      selection={inv.result}
                      onSelect={(candidate) => onSelectTemplate?.(candidate)}
                    />
                  </div>
                );
              }
              return (
                <div key={inv.toolCallId} className="flex flex-col gap-0.5">
                  <ToolCallBadge toolName={inv.toolName} args={inv.args} />
                  {inv.state === 'result' && (
                    <div className="pl-1">
                      <ToolResultSummary toolName={inv.toolName} result={inv.result} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Texto da mensagem */}
        {message.content && (
          <div
            className={cn(
              'rounded-xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words',
              isUser
                ? 'bg-[var(--accent)] text-[var(--accent-fg)] rounded-tr-sm'
                : 'bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] text-[var(--fg)] rounded-tl-sm',
            )}
          >
            {message.content}
          </div>
        )}
      </div>
    </div>
  );
}
